<template>
  <div data-testid="page-meetings-my-meetings">
    <MeetingsListLayout :loading="loading" :empty="appointments.length === 0" :groups="groupedAppointments">
      <template #toolbar>
        <h2 class="txt-headline-xl">{{ t('Meetings.myMeetings.title') }}</h2>
        <SingleSelect v-model="selectedDay" :options="dayFilterOptions" :label="t('Meetings.myMeetings.filterByDay')" class="w-48" />
      </template>

      <template #empty>
        <img src="@/assets/images/meetings-list-placeholder.svg" alt="" class="mb-4 h-40" />
        <i18n-t keypath="Meetings.myMeetings.noMeetings" tag="p" class="text-body-m text-subtle">
          <template #companyProfileLink>
            <a :href="companyProfileUrl" target="_blank" class="text-primary hover:underline">
              {{ t('Meetings.myMeetings.companyProfile') }}
            </a>
          </template>
        </i18n-t>
      </template>

      <template #card="{ item }">
        <MeetingCard
          :ref="(el) => setCardRef(item.id, el)"
          :appointment="item as AppointmentNode"
          collapsible
          @save-notes="handleSaveNotes"
          @cancel="handleCancelClick"
        />
      </template>
    </MeetingsListLayout>

    <!-- Cancel Dialog -->
    <MeetingCancelDialog :show="showCancelDialog" @confirm="handleCancelConfirm" @cancel="handleCancelCancel" />
  </div>
</template>

<script setup lang="ts">
import { MeetingsListLayout } from '@ramp106/omrjs-events-ui'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import MeetingCancelDialog from '@/components/Meetings/MeetingCancelDialog.vue'
import { type AppointmentNode, useMeetingsMyMeetings } from '@/composables/meetings/useMeetingsMyMeetings'
import { useMeStore } from '@/stores/me'
import SingleSelect from '@/ui/SingleSelect.vue'

import MeetingCard from './components/MeetingCard.vue'

const { t } = useI18n()

const { selectedDay, loading, appointments, groupedAppointments, dayFilterOptions, saveExhibitorNotes, cancelMeeting } =
  useMeetingsMyMeetings()

const meStore = useMeStore()
const companyProfileUrl = computed(() => {
  const slug = meStore.me?.partnerCompany?.slug
  return slug ? `https://omr.com/en/events/festival/exhibitors/${slug}` : '#'
})

// Card refs for resetting edit state
const cardRefs = ref<Record<string, InstanceType<typeof MeetingCard> | null>>({})

function setCardRef(id: string, el: unknown) {
  cardRefs.value[id] = el as InstanceType<typeof MeetingCard> | null
}

async function handleSaveNotes(appointmentId: string, notes: string) {
  const success = await saveExhibitorNotes(appointmentId, notes)
  cardRefs.value[appointmentId]?.resetEditingState(success)
}

// Cancel dialog state
const showCancelDialog = ref(false)
const pendingCancelId = ref<string | null>(null)

function handleCancelClick(appointmentId: string) {
  pendingCancelId.value = appointmentId
  showCancelDialog.value = true
}

async function handleCancelConfirm(reason: string) {
  if (pendingCancelId.value) {
    await cancelMeeting(pendingCancelId.value, reason || undefined)
  }
  showCancelDialog.value = false
  pendingCancelId.value = null
}

function handleCancelCancel() {
  showCancelDialog.value = false
  pendingCancelId.value = null
}
</script>
